import React, { useState } from 'react';
import { useLanguage } from '../lib/language';

const Resume: React.FC = () => {
  const { language, toggleLanguage } = useLanguage();
  const en = language === 'en';

  return (
    <>
      {/* ── Print + Base styles ───────────────────────────────────────── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=EB+Garamond:wght@400;700&family=Lato:ital,wght@0,400;0,700;1,400&display=swap');

        .resume-root * { box-sizing: border-box; margin: 0; padding: 0; }

        .resume-root {
          font-family: 'Lato', 'Calibri', 'Arial', sans-serif;
          font-size: 11.5px;
          line-height: 1.45;
          color: #000;
          background: #fff;
          min-height: 100vh;
          padding: 48px 0;
        }

        .resume-page {
          width: 760px;
          margin: 0 auto;
          background: #fff;
          color: #000;
        }

        /* ── Header ── */
        .resume-name {
          font-family: 'EB Garamond', Georgia, serif;
          font-size: 32px;
          font-weight: 700;
          text-align: center;
          letter-spacing: 1.5px;
          margin-bottom: 6px;
          color: #000;
        }

        .resume-contact {
          text-align: center;
          font-size: 10.5px;
          color: #000;
          margin-bottom: 14px;
        }

        .resume-contact a {
          color: #000;
          text-decoration: none;
        }

        /* ── Section ── */
        .resume-section {
          margin-top: 14px;
        }

        .resume-section-title {
          font-size: 11.5px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.8px;
          color: #000;
          border-bottom: 1.5px solid #000;
          padding-bottom: 3px;
          margin-bottom: 8px;
        }

        /* ── Summary ── */
        .resume-summary {
          font-size: 11px;
          line-height: 1.5;
          color: #000;
          margin-bottom: 2px;
        }

        /* ── Entry ── */
        .resume-entry {
          margin-bottom: 10px;
        }

        .resume-entry-header {
          display: flex;
          justify-content: space-between;
          align-items: baseline;
        }

        .resume-company {
          font-weight: 700;
          font-size: 11.5px;
          color: #000;
        }

        .resume-location {
          font-size: 11px;
          font-weight: 700;
          color: #000;
          text-align: right;
        }

        .resume-role-row {
          display: flex;
          justify-content: space-between;
          align-items: baseline;
          margin-top: 1px;
        }

        .resume-role {
          font-style: italic;
          font-size: 11px;
          color: #000;
        }

        .resume-dates {
          font-style: italic;
          font-size: 10.5px;
          color: #000;
          text-align: right;
        }

        .resume-subtitle {
          font-size: 10.5px;
          color: #333;
          margin-top: 1px;
          margin-bottom: 3px;
        }

        .resume-bullets {
          margin-top: 3px;
          margin-left: 16px;
          list-style-type: disc;
        }

        .resume-bullets li {
          font-size: 11px;
          margin-bottom: 2px;
          line-height: 1.45;
        }

        /* ── Previous experience ── */
        .resume-previous {
          font-size: 10.5px;
          color: #000;
          margin-top: 4px;
        }

        /* ── Projects ── */
        .resume-project-name {
          font-weight: 700;
          font-size: 11.5px;
        }

        .resume-project-tag {
          font-style: italic;
          font-weight: 400;
          font-size: 10.5px;
          color: #444;
          margin-left: 4px;
        }

        .resume-project-desc {
          font-size: 11px;
          margin-top: 2px;
          line-height: 1.45;
        }

        .resume-stack {
          font-size: 10px;
          font-style: italic;
          color: #333;
          margin-top: 2px;
        }

        .resume-project-link {
          font-size: 10px;
          color: #000;
          margin-top: 1px;
        }

        /* ── Skills inline ── */
        .resume-skills-line {
          font-size: 11px;
          line-height: 1.6;
        }

        .resume-skills-label {
          font-weight: 700;
          font-style: italic;
        }

        /* ── Education ── */
        .resume-edu-degree {
          font-style: italic;
          font-size: 11px;
        }

        /* ── Certs ── */
        .resume-certs {
          font-size: 11px;
          line-height: 1.6;
        }

        /* ── Controls (no print) ── */
        .resume-controls {
          position: fixed;
          top: 20px;
          right: 24px;
          display: flex;
          gap: 8px;
          z-index: 100;
        }

        .resume-btn {
          background: #000;
          color: #fff;
          border: none;
          padding: 6px 14px;
          font-size: 11px;
          font-family: 'Lato', sans-serif;
          font-weight: 700;
          letter-spacing: 0.5px;
          cursor: pointer;
          border-radius: 2px;
        }

        .resume-btn:hover { background: #333; }

        /* ── Print ── */
        @media print {
          .resume-controls { display: none !important; }
          .resume-root { padding: 0; background: #fff; }
          .resume-page { width: 100%; margin: 0; }
          body { margin: 0; background: #fff; }
          * { -webkit-print-color-adjust: exact; }
        }
      `}</style>

      {/* Controls */}
      <div className="resume-controls">
        <button className="resume-btn" onClick={toggleLanguage}>
          {en ? 'ES' : 'EN'}
        </button>
        <button className="resume-btn" onClick={() => window.print()}>
          ↓ PDF
        </button>
      </div>

      <div className="resume-root">
        <div className="resume-page">

          {/* ── HEADER ── */}
          <h1 className="resume-name">Erick G. Manrique Vargas</h1>
          <p className="resume-contact">
            Lima, Perú &nbsp;•&nbsp; +51 990 033 972 &nbsp;•&nbsp;{' '}
            <a href="mailto:erick.manrique112@gmail.com">erick.manrique112@gmail.com</a>
            &nbsp;•&nbsp;{' '}
            <a href="https://github.com/erickManVar" target="_blank" rel="noreferrer">github.com/erickManVar</a>
            &nbsp;•&nbsp;{' '}
            <a href="https://musashi.studio" target="_blank" rel="noreferrer">musashi.studio</a>
          </p>

          {/* ── SUMMARY ── */}
          <div className="resume-section">
            <p className="resume-summary">
              {en
                ? 'Full-stack developer and product designer with an AI-specialist profile. Combines product thinking, software engineering, and applied AI — including LLM fine-tuning, RAG systems, autonomous agents, and MCP integrations. Experienced building SaaS platforms, marketplaces, and client-facing products from zero to production, bridging technical execution with business logic.'
                : 'Desarrollador full-stack y product designer con perfil especialista en IA. Combina product thinking, ingeniería de software e IA aplicada — incluyendo fine-tuning de LLMs, sistemas RAG, agentes autónomos e integraciones MCP. Experiencia construyendo plataformas SaaS, marketplaces y productos digitales desde cero hasta producción.'
              }
            </p>
          </div>

          {/* ── EDUCATION ── */}
          <div className="resume-section">
            <div className="resume-section-title">{en ? 'Education' : 'Educación'}</div>

            <div className="resume-entry">
              <div className="resume-entry-header">
                <span className="resume-company">Universidad Nacional Mayor de San Marcos</span>
                <span className="resume-location">Lima, Perú</span>
              </div>
              <div className="resume-role-row">
                <span className="resume-edu-degree">
                  {en ? 'B.S. in Economics, In Progress (7th semester)' : 'Economía, En curso (7° semestre)'}
                </span>
                <span className="resume-dates">2015 – {en ? 'Present' : 'Presente'}</span>
              </div>
            </div>

            <div className="resume-entry">
              <div className="resume-entry-header">
                <span className="resume-company">Cibertec</span>
                <span className="resume-location">Lima, Perú</span>
              </div>
              <div className="resume-role-row">
                <span className="resume-edu-degree">
                  {en ? 'Technical Degree in Computer Science, In Progress — Final Semester' : 'Computación e Informática, En curso — Último semestre'}
                </span>
                <span className="resume-dates">2023 – {en ? 'Present' : 'Presente'}</span>
              </div>
            </div>
          </div>

          {/* ── WORK EXPERIENCE ── */}
          <div className="resume-section">
            <div className="resume-section-title">{en ? 'Work Experience' : 'Experiencia'}</div>

            {/* Indoor Solutions */}
            <div className="resume-entry">
              <div className="resume-entry-header">
                <span className="resume-company">Indoor Solutions SAC</span>
                <span className="resume-location">Lima, Perú</span>
              </div>
              <div className="resume-role-row">
                <span className="resume-role">Product Designer &amp; Full-Stack Developer</span>
                <span className="resume-dates">Jul 2025 – {en ? 'Present' : 'Presente'}</span>
              </div>
              <ul className="resume-bullets">
                {en ? <>
                  <li>Designed and developed interactive digital invitation platforms with Three.js, 3D animations, and personalized client experiences</li>
                  <li>Built complete backoffice systems for event and invitation management</li>
                  <li>Translated complex client requirements into clear, elegant product interfaces</li>
                  <li>Led end-to-end product development for two flagship SaaS products: Nexa (dental clinic management) and AiJ Aula Virtual (e-learning platform)</li>
                </> : <>
                  <li>Diseñé y desarrollé plataformas de invitaciones digitales interactivas con Three.js, animaciones 3D y experiencias personalizadas</li>
                  <li>Construí sistemas de backoffice completos para gestión de eventos e invitaciones</li>
                  <li>Traduje requerimientos complejos del cliente en interfaces de producto claras y elegantes</li>
                  <li>Lideré el desarrollo end-to-end de dos productos SaaS flagship: Nexa (gestión de clínicas dentales) y AiJ Aula Virtual (plataforma e-learning)</li>
                </>}
              </ul>
            </div>

            {/* Finnerve */}
            <div className="resume-entry">
              <div className="resume-entry-header">
                <span className="resume-company">Finnerve</span>
                <span className="resume-location">Lima, Perú</span>
              </div>
              <div className="resume-role-row">
                <span className="resume-role">Full-Stack Developer</span>
                <span className="resume-dates">Jan 2025 – Jun 2025</span>
              </div>
              <p className="resume-subtitle">{en ? 'Fintech specialized in factoring & confirming' : 'Fintech especializada en factoring & confirming'}</p>
              <ul className="resume-bullets">
                {en ? <>
                  <li>Developed and optimized financial software handling complex, data-heavy interfaces</li>
                  <li>Designed intuitive dashboards for dense financial workflows and reporting</li>
                  <li>Applied economics background to improve system logic and business rules</li>
                </> : <>
                  <li>Desarrollé y optimicé software financiero con interfaces de alta densidad de datos</li>
                  <li>Diseñé dashboards intuitivos para flujos financieros complejos y reportería</li>
                  <li>Apliqué conocimientos de economía para mejorar la lógica del sistema y reglas de negocio</li>
                </>}
              </ul>
            </div>

            {/* Coral Dental */}
            <div className="resume-entry">
              <div className="resume-entry-header">
                <span className="resume-company">Coral Dental Care</span>
                <span className="resume-location">Lima, Perú</span>
              </div>
              <div className="resume-role-row">
                <span className="resume-role">Medical Interpreter</span>
                <span className="resume-dates">May 2024 – Dec 2024</span>
              </div>
              <ul className="resume-bullets">
                {en ? <>
                  <li>Provided precise English-Spanish medical interpretation for dental procedures</li>
                  <li>Ensured accurate communication between patients and healthcare professionals</li>
                </> : <>
                  <li>Brindé interpretación médica precisa inglés-español para procedimientos odontológicos</li>
                  <li>Garanticé comunicación exacta entre pacientes y profesionales de salud</li>
                </>}
              </ul>
            </div>

            {/* Previous */}
            <p className="resume-previous">
              <span style={{ fontStyle: 'italic' }}>{en ? 'Previous:' : 'Anterior:'}</span>{' '}
              Amistad Insurance — {en ? 'Admin Support & Trainee Developer (2023–2024)' : 'Soporte Administrativo & Dev Trainee (2023–2024)'} &nbsp;·&nbsp;
              Netflix, Instacart, DoorDash — {en ? 'Bilingual Customer Service (2017–2023)' : 'Atención al cliente bilingüe (2017–2023)'}
            </p>
          </div>

          {/* ── SELECTED PROJECTS ── */}
          <div className="resume-section">
            <div className="resume-section-title">{en ? 'Selected Projects' : 'Proyectos'}</div>

            <div className="resume-entry">
              <span className="resume-project-name">Nexa</span>
              <span className="resume-project-tag">— {en ? 'Dental SaaS Platform' : 'Plataforma SaaS Dental'} · {en ? 'Private client work' : 'Proyecto privado'}</span>
              <p className="resume-project-desc">
                {en
                  ? 'Full-stack SaaS for dental clinic management: digital patient records, SUNAT billing integration, AI-powered live transcription, financial reporting, and clinic dashboard. Trusted by 500+ clinics in Lima.'
                  : 'SaaS full-stack para gestión de consultorios dentales: historial clínico digital, facturación SUNAT, transcripción en vivo con IA, reportería financiera. Usado por más de 500 clínicas en Lima.'}
              </p>
              <p className="resume-stack">Next.js · TypeScript · PostgreSQL · Prisma · Radix UI · Tailwind CSS</p>
            </div>



            <div className="resume-entry">
              <span className="resume-project-name">Musashi Studio Marketplaces</span>
              <span className="resume-project-tag">— github.com/erickManVar</span>
              <p className="resume-project-desc">
                {en
                  ? '3 production-ready full-stack marketplaces (vetpe.com, motorperu.com, midefensaperu.com) with escrow payments, real-time chat, admin backoffices, provider verification, and multi-role auth.'
                  : '3 marketplaces full-stack listos para producción (vetpe.com, motorperu.com, midefensaperu.com) con pagos escrow, chat en tiempo real, backoffices de administración y autenticación multi-rol.'}
              </p>
              <p className="resume-stack">NestJS · Fastify · Drizzle ORM · Neon · Culqi · better-auth · Next.js · Railway · Vercel</p>
            </div>

            <div className="resume-entry">
              <span className="resume-project-name">San Marcos Engine</span>
              <span className="resume-project-tag">— san-marcos-dashboard.vercel.app</span>
              <p className="resume-project-desc">
                {en
                  ? 'Exam prep engine: 1,661 parsed and verified questions from official UNMSM PDFs, automatic topic classification, diagnostic mode, and interactive study dashboard.'
                  : 'Motor de preparación para examen UNMSM: 1,661 preguntas parseadas y verificadas de PDFs oficiales, clasificación automática por tema, modo diagnóstico y dashboard interactivo.'}
              </p>
              <p className="resume-stack">Next.js · Python · pymupdf · Vercel</p>
            </div>
          </div>

          {/* ── AI TOOLING ── */}
          <div className="resume-section">
            <div className="resume-section-title">AI Tooling &amp; Practice</div>

            <p className="resume-skills-line">
              <span className="resume-skills-label">{en ? 'Applied: ' : 'Aplicado: '}</span>
              OpenAI API · Anthropic Claude · Hermes Agent · Claude Code · OpenCode · MCP integrations &amp; custom skills · Autonomous agents &amp; workflows · Prompt engineering · AI image generation · Windsurf IDE (beta tester) · Hugging Face Hub · RAG systems (LangChain · ChromaDB) · Fine-tuning workflows
            </p>
            <p className="resume-skills-line" style={{ marginTop: 4 }}>
              <span className="resume-skills-label">{en ? 'Local LLMs: ' : 'LLMs locales: '}</span>
              {en
                ? 'Running and optimizing open-source models locally — Qwen 2.5 · Llama 3 · DeepSeek R1 — via Ollama / llama.cpp (GGUF quantization)'
                : 'Ejecución y optimización de modelos open-source en local — Qwen 2.5 · Llama 3 · DeepSeek R1 — vía Ollama / llama.cpp (cuantización GGUF)'}
            </p>
          </div>

          {/* ── TECHNICAL SKILLS ── */}
          <div className="resume-section">
            <div className="resume-section-title">{en ? 'Technical Skills' : 'Habilidades Técnicas'}</div>
            <p className="resume-skills-line">
              <span className="resume-skills-label">{en ? 'Languages & Frameworks: ' : 'Lenguajes y Frameworks: '}</span>
              TypeScript · JavaScript · Python · Swift · Kotlin · Flutter · NestJS · Next.js · React · Three.js
            </p>
            <p className="resume-skills-line" style={{ marginTop: 4 }}>
              <span className="resume-skills-label">{en ? 'Databases & Tools: ' : 'Bases de datos y herramientas: '}</span>
              PostgreSQL · Drizzle ORM · Prisma · Neon · Tailwind CSS · Git · Docker
            </p>
            <p className="resume-skills-line" style={{ marginTop: 4 }}>
              <span className="resume-skills-label">{en ? 'Languages: ' : 'Idiomas: '}</span>
              {en ? 'Spanish (native) · English (fluent)' : 'Español (nativo) · Inglés (fluido)'}
            </p>
          </div>

          {/* ── CERTIFICATIONS ── */}
          <div className="resume-section">
            <div className="resume-section-title">{en ? 'Certifications' : 'Certificaciones'}</div>
            <p className="resume-certs">
              AI Agents &amp; Automation (OpenAI) &nbsp;·&nbsp;
              Prompt Engineering for Business (OpenAI / Coursera) &nbsp;·&nbsp;
              Meta Business &amp; Ads (Meta) &nbsp;·&nbsp;
              Stata for Economists (PUCP) &nbsp;·&nbsp;
              iOS Development (Swift / MVVM) &nbsp;·&nbsp;
              Kotlin &nbsp;·&nbsp; Flutter &nbsp;·&nbsp; Git
            </p>
          </div>

        </div>{/* /resume-page */}
      </div>{/* /resume-root */}
    </>
  );
};

export default Resume;
